/**
 * MobileCarousel Utility Functions
 *
 * This module contains utility functions for the MobileCarousel component,
 * including device-specific calculations, scroll logic, and analytics helpers.
 */

// Device-specific configuration constants
export const DEVICE_CONFIG = {
  MOBILE: {
    PODS_PER_SLIDE: 2,
    SCROLL_MULTIPLICANT: 3,
    INDICATOR_CALCULATION: 'floor', // Conservative count for mobile
  },
  TABLET: {
    PODS_PER_SLIDE: 4,
    SCROLL_MULTIPLICANT: 1.5,
    INDICATOR_CALCULATION: 'ceil',
  },
  DESKTOP: {
    PODS_PER_SLIDE: 6,
    SCROLL_MULTIPLICANT: 1,
    INDICATOR_CALCULATION: 'ceil',
  },
};

// Business logic constants
export const BUSINESS_RULES = {
  MIN_INDICATORS_TO_SHOW: 1,
  MIN_PRODUCTS_FOR_SLIDE_BUTTON: 6,
  INTERSECTION_THRESHOLD: 0.3,
  LAZY_LOAD_THRESHOLD: 0.1,
};

// Analytics constants
export const ANALYTICS_CONFIG = {
  IMPRESSION_THRESHOLD: 0.3,
  CLICK_TRACKING_ENABLED: true,
  SPONSORED_TRACKING_ENABLED: true,
};

export const ANALYTICS_CONSTANTS = {
  SPONSORED_CAROUSEL_AD_TYPE: 'sponsored_carousel',
  HYBRID_CAROUSEL_AD_TYPE: 'hybrid_carousel',
};

/**
 * Calculates the number of indicators needed based on device type and product count
 *
 * @param {string} deviceType - The current device type ('mobile', 'tablet', 'desktop')
 * @param {number} productCount - Total number of products
 * @returns {number} Number of indicators to display
 */
export const calculateIndicatorCount = (deviceType, productCount) => {
  // Input validation
  if (typeof productCount !== 'number' || productCount < 0) {
    return 0;
  }

  // Get device configuration with fallback to desktop
  const deviceKey = deviceType?.toUpperCase() || 'DESKTOP';
  const config = DEVICE_CONFIG[deviceKey] || DEVICE_CONFIG.DESKTOP;

  const { PODS_PER_SLIDE, INDICATOR_CALCULATION } = config;

  // Calculate indicators based on device-specific method
  const indicatorCount =
    INDICATOR_CALCULATION === 'floor'
      ? Math.floor(productCount / PODS_PER_SLIDE)
      : Math.ceil(productCount / PODS_PER_SLIDE);

  // Ensure minimum of 0 indicators
  return Math.max(0, indicatorCount);
};

/**
 * Determines if indicators should be shown
 * Mobile devices use native scroll, so indicators are hidden
 * Only show indicators when there are multiple slides
 *
 * @param {boolean} isSponsored - Whether the carousel contains sponsored content
 * @param {number} indicatorCount - Number of indicators calculated
 * @param {string} deviceType - Current device type
 * @returns {boolean} Whether to show indicators
 */
export const shouldShowIndicators = (indicatorCount, deviceType) => {
  // Mobile uses native scroll, no indicators needed
  if (deviceType === 'mobile') return false;

  return indicatorCount > BUSINESS_RULES.MIN_INDICATORS_TO_SHOW;
};

/**
 * Determines if navigation arrows should be shown
 * Only show arrows on desktop when there are multiple slides
 *
 * @param {string} deviceType - Current device type
 * @param {number} indicatorCount - Number of indicators calculated
 * @returns {boolean} Whether to show navigation arrows
 */
export const shouldShowNavigationArrows = (deviceType, indicatorCount) => {
  // Only desktop has navigation arrows
  if (deviceType !== 'desktop') return false;

  // Only show arrows if there are multiple slides
  return indicatorCount > 1;
};

/**
 * Determines if slide button should be shown based on product count
 *
 * @param {number} productCount - Total number of products
 * @returns {boolean} Whether to show slide button
 */
export const shouldShowSlideButton = (productCount) => {
  return productCount > BUSINESS_RULES.MIN_PRODUCTS_FOR_SLIDE_BUTTON;
};

/**
 * Calculates scroll multiplicant for mobile scroll events
 * Based on original carousel logic
 *
 * @param {string} deviceType - The current device type
 * @returns {number} Multiplicant for scroll calculations
 */
export const getScrollMultiplicant = (deviceType) => {
  const deviceKey = deviceType.toUpperCase();
  const config = DEVICE_CONFIG[deviceKey] || DEVICE_CONFIG.DESKTOP;
  return config.SCROLL_MULTIPLICANT;
};

/**
 * Determines if scroll event should be attached based on device type
 * Mobile and tablet use scroll events, desktop uses button navigation
 *
 * @param {string} deviceType - The current device type
 * @returns {boolean} Whether to attach scroll event listener
 */
export const shouldAttachScrollEvent = (deviceType) => {
  // Desktop uses button navigation, mobile/tablet use scroll events
  return deviceType !== 'desktop';
};

/**
 * Calculates the current slide index based on scroll position
 *
 * @param {number} scrollLeft - Current scroll position
 * @param {number} clientWidth - Container client width
 * @param {number} multiplicant - Device-specific multiplicant
 * @returns {number} Current slide index
 */
export const calculateSlideIndex = (scrollLeft, clientWidth, multiplicant) => {
  const scrollUnit = multiplicant * clientWidth;
  return Math.floor(scrollLeft / scrollUnit);
};

/**
 * Determines navigation button states for desktop
 *
 * @param {number} currentIndex - Current slide index
 * @param {number} maxIndex - Maximum slide index
 * @returns {Object} Object with canScrollPrev and canScrollNext properties
 */
export const getNavigationStates = (currentIndex, maxIndex) => {
  const FIRST_SLIDE_INDEX = 0;
  return {
    canScrollPrev: currentIndex > FIRST_SLIDE_INDEX,
    canScrollNext: currentIndex < maxIndex,
  };
};

/**
 * Validates and processes products array
 *
 * @param {Array} products - Raw products array
 * @returns {Array} Processed products array
 */
export const processProducts = (products) => {
  const EMPTY_PRODUCTS_ARRAY = [];

  if (!Array.isArray(products)) {
    return EMPTY_PRODUCTS_ARRAY;
  }

  return products.filter((product) => product && typeof product === 'object');
};

/**
 * Generates analytics data for carousel interactions
 *
 * @param {Object} product - Product object
 * @param {number} position - Product position in carousel
 * @param {string} carouselPosition - Carousel position identifier
 * @param {string} deviceType - Current device type
 * @returns {Object} Analytics data object
 */
export const generateAnalyticsData = (product, position, carouselPosition, deviceType) => {
  return {
    product,
    position,
    carouselPosition,
    deviceType,
    timestamp: Date.now(),
    trackingEnabled: ANALYTICS_CONFIG.CLICK_TRACKING_ENABLED,
  };
};
